<template>
  <div class="mensageria-models-list-wrap">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h5 class="mb-0 mensageria-section-title">Modelos de notificação</h5>
      <span v-if="!loading" class="text-muted small">
        {{ models.length }} modelo(s) · status=3
      </span>
    </div>

    <slot name="env-warning" />

    <div v-if="loading" class="text-muted">
      <i class="fas fa-spinner fa-spin mr-1"></i>
      Carregando modelos...
    </div>

    <base-alert v-else-if="error" type="warning" class="w-100">
      {{ error }}
    </base-alert>

    <div v-else-if="!models.length" class="text-muted">
      Nenhum modelo (<code>notifications.status = 3</code>) encontrado.
    </div>

    <div v-else class="list-group mensageria-models-list">
      <div
        v-for="model in models"
        :key="model.uuid || model.id"
        class="list-group-item"
      >
        <div class="d-flex align-items-start">
          <div class="flex-grow-1 pr-2">
            <div class="font-weight-bold">
              <span
                v-if="hasMissingContentVars(model)"
                class="mensageria-models-list__warn mr-1"
                :title="missingVarsTitle(model)"
                aria-label="Variáveis ausentes em message_data"
              >
                !
              </span>
              {{ modelTitle(model) }}
              <span
                v-if="model.mappedInEnv === false"
                class="badge badge-warning ml-1 font-weight-normal"
                title="Sem chave MODELO_* no .env"
              >
                sem .env
              </span>
            </div>
            <div class="small text-muted">
              <span v-if="model.channel">{{ model.channel }}</span>
              <span v-if="model.modelName || model.model_name" class="ml-1">
                · {{ model.modelName || model.model_name }}
              </span>
              <span
                v-if="model.envKeys && model.envKeys.length"
                class="ml-1"
              >
                · {{ model.envKeys.join(', ') }}
              </span>
              <span v-else-if="model.placeUuid" class="ml-1">
                · place {{ shortUuid(model.placeUuid) }}
              </span>
            </div>
          </div>
          <div class="btn-group btn-group-sm">
            <button
              type="button"
              class="btn btn-outline-secondary"
              title="Ver detalhes"
              @click="toggleExpand(model)"
            >
              <i
                class="fa"
                :class="isExpanded(model) ? 'fa-chevron-up' : 'fa-eye'"
              ></i>
            </button>
          </div>
        </div>

        <div v-if="isExpanded(model)" class="mt-2">
          <div class="mensageria-models-list__block-label mb-1">
            Modelo de mensagem
          </div>
          <pre class="model-json mb-3 small">{{
            formatModelMessage(model)
          }}</pre>

          <div class="mensageria-models-list__block-label mb-1">
            Dados da mensagem
          </div>
          <pre class="model-json mb-0 small">{{
            formatMessageData(model)
          }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  parseNotificationMessage,
  messageDataCatalogFromModel,
  findMissingContentVars,
} from './utils/messageData'

export default {
  name: 'MensageriaModelsList',
  props: {
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    /** AdminModelItem[] de GET /messenger/admin/models (notifications.status=3) */
    models: { type: Array, default: () => [] },
  },
  data() {
    return {
      expandedKey: null,
    }
  },
  methods: {
    modelKey(model) {
      return (model && (model.uuid || String(model.id))) || null
    },
    modelTitle(model) {
      return (
        (model &&
          (model.title || model.modelName || model.model_name || model.uuid)) ||
        'Modelo'
      )
    },
    shortUuid(uuid) {
      if (!uuid) return ''
      return String(uuid).slice(0, 8)
    },
    isExpanded(model) {
      return this.expandedKey && this.expandedKey === this.modelKey(model)
    },
    toggleExpand(model) {
      const key = this.modelKey(model)
      if (!key) return
      this.expandedKey = this.expandedKey === key ? null : key
    },
    modelCaption(model) {
      return parseNotificationMessage(model && model.message) || {}
    },
    formatJson(value) {
      try {
        return JSON.stringify(value == null ? {} : value, null, 2)
      } catch (e) {
        return String(value)
      }
    },
    formatModelMessage(model) {
      const caption = this.modelCaption(model)
      return this.formatJson(caption.model_message || {})
    },
    formatMessageData(model) {
      const catalog = messageDataCatalogFromModel(model)
      // Prefer raw message_data object (incl. non-string) when present
      const caption = this.modelCaption(model)
      const raw =
        caption.message_data && typeof caption.message_data === 'object'
          ? caption.message_data
          : catalog
      return this.formatJson(raw)
    },
    missingContentVars(model) {
      return findMissingContentVars(model)
    },
    hasMissingContentVars(model) {
      return this.missingContentVars(model).length > 0
    },
    missingVarsTitle(model) {
      const missing = this.missingContentVars(model)
      if (!missing.length) return ''
      return (
        'Variáveis no conteúdo sem entrada em message_data: ' +
        missing.join(', ')
      )
    },
  },
}
</script>

<style scoped>
.mensageria-section-title {
  color: #12576e;
  font-weight: 600;
}

.mensageria-models-list__warn {
  display: inline-block;
  color: #f0ad4e;
  font-weight: 800;
  font-size: 1.05em;
  line-height: 1;
  cursor: help;
}

.mensageria-models-list__block-label {
  color: #12576e;
  font-weight: 600;
  font-size: 0.85rem;
}

.model-json {
  max-height: 240px;
  overflow: auto;
  background: #f8fafb;
  border: 1px solid #e6eef2;
  border-radius: 4px;
  padding: 0.75rem;
  white-space: pre-wrap;
  word-break: break-word;
}

.mensageria-models-list .list-group-item {
  border-color: #e6eef2;
}
</style>
